import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildSummary, GuildListResult } from '@/lib/api';

interface UseGuildsOptions {
  search?: string;
  guildType?: string;
  sortBy?: string;
}

interface UseGuildsReturn {
  guilds: GuildSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useGuilds(options: UseGuildsOptions = {}): UseGuildsReturn {
  const [guilds, setGuilds] = useState<GuildSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchGuilds = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result: GuildListResult = await api.listGuilds({
          search: options.search || undefined,
          guildType: options.guildType || undefined,
          sortBy: options.sortBy || 'newest',
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setGuilds((prev) => [...prev, ...result.guilds]);
        } else {
          setGuilds(result.guilds);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load guilds');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.search, options.guildType, options.sortBy],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchGuilds(0, false);
  }, [fetchGuilds]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchGuilds(offset + limit, true);
    }
  }, [fetchGuilds, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    guilds,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
